import { BuyableItemType, DiscountType, Prisma } from "@prisma/client";

export const simplePriceValues = Prisma.validator<Prisma.PriceDefaultArgs>()({
  select: {
    priceTTC: true,
    vatRate: true,
  },
});

export type SimplePrice = Prisma.PriceGetPayload<typeof simplePriceValues>;

const editablePriceValues = Prisma.validator<Prisma.PriceDefaultArgs>()({
  select: {
    id: true,
    ...simplePriceValues.select,
  },
});

export const simplePricingValues = Prisma.validator<Prisma.PricingDefaultArgs>()({
  select: {
    isPaid: true,
    price: simplePriceValues,
  },
});

export type SimplePricing = Prisma.PricingGetPayload<typeof simplePricingValues>;

const discountSelect = {
  type: true,
  value: true,
} satisfies Prisma.DiscountSelect;

const editableDiscountSelect = {
  ...discountSelect,
  id: true,
} satisfies Prisma.DiscountSelect;

// Never add name to this select !
const discountCodeSelect = {
  id: true,
  discount: {
    select: discountSelect,
  },
} satisfies Prisma.DiscountCodeSelect;

export const computablePricingValues = Prisma.validator<Prisma.PricingDefaultArgs>()({
  select: {
    id: true,
    isPaid: true,
    price: simplePriceValues,
    discountCodes: {
      where: {
        discountCode: {
          deletedAt: null,
        },
      },
      select: {
        discountCode: {
          select: discountCodeSelect,
        },
      },
    },
    ticketing: {
      select: {
        eventDiscountCodes: {
          where: {
            deletedAt: null,
            disabledAt: null,
          },
          select: discountCodeSelect,
        },
      },
    },
    degressivePrices: {
      select: {
        id: true,
        minQuantity: true,
        maxQuantity: true,
        onQuote: true,
        discount: {
          select: editableDiscountSelect,
        },
      },
      orderBy: {
        minQuantity: {
          sort: "asc",
          nulls: "first",
        },
      },
    },
  },
});

export type ComputablePricing = Prisma.PricingGetPayload<typeof computablePricingValues>;

export const editablePricingValues = Prisma.validator<Prisma.PricingDefaultArgs>()({
  select: {
    id: true,
    isPaid: true,
    price: editablePriceValues,
    discountCodes: {
      where: {
        discountCode: {
          deletedAt: null,
        },
      },
      select: {
        quantityLeft: true,
        discountCode: {
          select: {
            id: true,
            quantity: true,
            name: true,
            isCumulable: true,
            isQuantityInfinite: true,
            maxUsesPerVisitor: true,
            validityDates: true,
            discount: {
              select: editableDiscountSelect,
            },
          },
        },
      },
    },
    ticketing: {
      select: {
        eventDiscountCodes: {
          where: {
            deletedAt: null,
            disabledAt: null,
          },
          select: discountCodeSelect,
        },
      },
    },
    degressivePrices: {
      select: {
        id: true,
        minQuantity: true,
        maxQuantity: true,
        onQuote: true,
        discount: {
          select: editableDiscountSelect,
        },
      },
      orderBy: {
        minQuantity: "asc",
      },
    },
  },
});

export type EditablePricing = Prisma.PricingGetPayload<typeof editablePricingValues>;

export const safeComputablePricingValues = Prisma.validator<Prisma.PricingDefaultArgs>()({
  ...computablePricingValues,
  select: {
    ...computablePricingValues.select,
    discountCodes: {
      ...computablePricingValues.select?.discountCodes,
      where: {
        discountCode: {
          id: { in: [] },
        },
      },
    },
  },
});

export type DetailedFinalPrice = {
  basePrice: number;
  finalPrice: number;
  vatRate: number;
  codeDiscount: number;
  degressiveDiscount: number;
  appliedDegressivePrice: {
    id: string;
    minQuantity: number | null;
    maxQuantity: number | null;
    discountType: DiscountType;
    discountValue: number;
    triggeredQuantity: number;
  } | null;
  appliedCode: {
    id: string;
    codeName: string;
    discountType: DiscountType;
    discountValue: number;
    appliedAmount: number;
    isCumulable: boolean;
    quantityLeft: number | null;
    discountPrestations: {
      prestationType: BuyableItemType;
      quantityLeft: number | null;
    }[];
  }[];
};
